import { useMemo } from "react";

import { initialCarrierSelectionValues } from "~/features/quote/components/carrier-selection/constants/carrier-selection-constants.ts";
import { TenderModal } from "~/features/quote/components/shipment-details/components/tender-modal/tender-modal.tsx";
import type {
	QuoteAddressContextType,
	SelectedAddressBookItems,
} from "~/features/quote/context/quote-address-context.tsx";
import { QuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import type {
	QuoteDetailsContextType,
	QuoteStateType,
} from "~/shared/types/quote-details-types.ts";

import type { AugmentedShipment } from "../../types/augmented-shipment";
import {
	buildShipmentAddressBookItems,
	buildShipmentQuoteState,
} from "../../utils/shipment-quote-context-adapter.ts";

type LtlDocumentsTenderModalProps = {
	showModal: boolean;
	onToggleModal: () => void;
	quoteState: QuoteStateType;
	selectedAddressBookItems: SelectedAddressBookItems;
};

// Reuses the create-quote `TenderModal` as-is (View/Download/Send/Subscribe to
// Notifications) by feeding it a `QuoteDetailsContext`/`QuoteAddressContext`
// built from an existing shipment/load instead of an in-progress quote. See
// `shipment-quote-context-adapter.ts` for which fields are real vs. placeholder.
export const LtlDocumentsTenderModal = ({
	showModal,
	onToggleModal,
	quoteState,
	selectedAddressBookItems,
}: LtlDocumentsTenderModalProps) => {
	const quoteDetailsValue = useMemo<QuoteDetailsContextType>(
		() => ({
			quoteState,
			setQuoteState: () => {},
			resetForm: () => {},
			onChange: () => {},
			addItem: () => {},
			removeItem: () => {},
			updateItem: () => {},
			carrierSelectionState: initialCarrierSelectionValues,
			handleCarrierFilterAction: () => {},
			handleCarrierGlobalMarginAction: () => {},
			handleCarrierAction: () => {},
			handleItemUpdate: () => {},
			setCustomerDetails: () => {},
			updateItemsAndSummary: async () => {},
			setQuoteTotals: () => {},
			mergeResponseIntoQuoteState: () => {},
			loadQuoteFromResponse: () => {},
			imperialMetricConversion: () => {},
			setCreditDetails: () => {},
			updateAllCarrierPriceMargin: () => {},
			applyInsuranceToQuote: () => {},
			addBlankCarrier: () => {},
			removeBlankCarrier: () => {},
			updateCarrierPriceMargin: () => {},
			applyFoundCarrier: () => {},
			updateAdHocCarrier: () => {},
			setCustomerAr: () => {},
			removeAllBlankItems: () => {},
			workflow: "create_quote",
		}),
		[quoteState],
	);

	const quoteAddressValue = useMemo<QuoteAddressContextType>(
		() => ({
			selectedAddressBookItems,
			setSelectedAddressBookItem: () => {},
			updateSelectedAddressBookItem: () => {},
			resetSelectedAddressBookItem: () => {},
			resetSelectedAddressBookItems: () => {},
			addressChangeSourceRef: { current: "auto" },
		}),
		[selectedAddressBookItems],
	);

	return (
		<QuoteAddressContext.Provider value={quoteAddressValue}>
			<QuoteDetailsContext.Provider value={quoteDetailsValue}>
				<TenderModal
					onToggleModal={onToggleModal}
					showModal={showModal}
					title={"Generate LTL BOL and Labels"}
				/>
			</QuoteDetailsContext.Provider>
		</QuoteAddressContext.Provider>
	);
};

type GenerateLtlDocumentsModalProps = {
	showModal: boolean;
	onToggleModal: () => void;
	poNumber: number;
	shipment: AugmentedShipment;
};

export const GenerateLtlDocumentsModal = ({
	showModal,
	onToggleModal,
	poNumber,
	shipment,
}: GenerateLtlDocumentsModalProps) => {
	const quoteState = useMemo(
		() => buildShipmentQuoteState(shipment, poNumber),
		[shipment, poNumber],
	);
	const selectedAddressBookItems = useMemo(
		() => buildShipmentAddressBookItems(shipment),
		[shipment],
	);

	return (
		<LtlDocumentsTenderModal
			onToggleModal={onToggleModal}
			quoteState={quoteState}
			selectedAddressBookItems={selectedAddressBookItems}
			showModal={showModal}
		/>
	);
};
